import React, { useState } from "react";
import { Calculator, DollarSign, TrendingUp, Target, Wallet, Sparkles, Loader2, Search, Sliders } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import { Button } from "../components/ui/button";
import { computePricing } from "../lib/pricing";
import api from "../lib/apiClient";

const INPUTS = [
  { key: "cogs", label: "Cost per unit (COGS)", prefix: "$", min: 0, max: 500, step: 1 },
  { key: "margin", label: "Target profit margin", suffix: "%", min: 0, max: 95, step: 1 },
  { key: "overhead", label: "Monthly overhead", prefix: "$", min: 0, max: 50000, step: 100 },
  { key: "volume", label: "Monthly sales volume", suffix: " units", min: 0, max: 10000, step: 10 },
];

export default function PricingCalculator({ plan, value, onChange, readOnly }) {
  const [loading, setLoading] = useState(false);
  const [research, setResearch] = useState(null); // {recommended_price, rationale}
  const [adj, setAdj] = useState(0); // price sensitivity % adjustment
  const set = (key, val) => onChange({ ...value, [key]: val });
  const r = computePricing(value);

  const getRecommended = async () => {
    setLoading(true);
    try {
      const res = await api.post("/ai/pricing", {
        business_name: plan.business_name, industry: plan.industry, description: plan.description,
      });
      onChange({ ...value, ...res.data.pricing });
      setResearch({ recommended_price: res.data.recommended_price, rationale: res.data.rationale });
      toast.success("Pricing updated from market research");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pricing research failed");
    } finally {
      setLoading(false);
    }
  };

  const chartData = [
    { name: "Price", amount: Number(r.price), fill: "#f97316" },
    { name: "COGS", amount: Number(value.cogs) || 0, fill: "#38bdf8" },
    { name: "Margin", amount: Number(r.grossMargin), fill: "#10b981" },
  ];

  const outputs = [
    { label: "Recommended price", value: `$${r.price}`, icon: DollarSign, color: "text-orange-400" },
    { label: "Gross margin", value: `$${r.grossMargin} · ${r.marginPct}%`, icon: TrendingUp, color: "text-emerald-400" },
    { label: "Break-even / month", value: `${r.breakEven} units`, icon: Target, color: "text-sky-400" },
    { label: "Projected net profit", value: `$${Number(r.netProfit).toLocaleString()}/mo`, icon: Wallet, color: Number(r.netProfit) >= 0 ? "text-emerald-400" : "text-rose-400" },
  ];

  return (
    <div className="space-y-6">
      {!readOnly && (
        <div className="rounded-2xl border border-orange-500/30 bg-gradient-to-r from-orange-500/10 to-rose-500/10 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-orange-300 font-semibold"><Search className="h-4 w-4" /> Recommended price from live market research</div>
            <p className="text-sm text-slate-300 mt-1">
              {research ? <>Suggested retail price <b className="text-white">${research.recommended_price}</b> — {research.rationale}</> : "Let AI benchmark typical market rates and pre-fill your inputs below."}
            </p>
          </div>
          <Button data-testid="pricing-research-button" onClick={getRecommended} disabled={loading} className="cta-btn shrink-0">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />} {research ? "Re-run research" : "Get recommended pricing"}
          </Button>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
          <div className="flex items-center gap-2 mb-5">
            <Calculator className="h-5 w-5 text-orange-400" />
            <h3 className="font-display text-lg font-bold">Inputs</h3>
          </div>
          <div className="space-y-6">
            {INPUTS.map((f) => (
              <div key={f.key}>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm text-slate-300">{f.label}</label>
                  <div className="flex items-center gap-1 text-sm font-semibold text-slate-100">
                    {f.prefix}
                    <Input
                      data-testid={`pricing-${f.key}-input`}
                      type="number"
                      value={value[f.key]}
                      disabled={readOnly}
                      onChange={(e) => set(f.key, Number(e.target.value))}
                      className="w-24 h-8 bg-slate-800 border-slate-700 text-right"
                    />
                    {f.suffix}
                  </div>
                </div>
                {!readOnly && (
                  <Slider
                    data-testid={`pricing-${f.key}-slider`}
                    value={[Number(value[f.key]) || 0]}
                    min={f.min} max={f.max} step={f.step}
                    onValueChange={(v) => set(f.key, v[0])}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
          <h3 className="font-display text-lg font-bold mb-5">Live breakdown</h3>
          <div className="grid grid-cols-2 gap-3">
            {outputs.map((o) => (
              <div key={o.label} data-testid={`pricing-output-${o.label}`} className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
                <o.icon className={`h-4 w-4 ${o.color}`} />
                <div className={`font-display text-lg font-bold mt-2 ${o.color}`}>{o.value}</div>
                <div className="text-xs text-slate-400">{o.label}</div>
              </div>
            ))}
          </div>
          <div className="h-52 mt-6">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <BarChart data={chartData}>
                <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 12, color: "#fff" }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                <Bar dataKey="amount" radius={[8, 8, 0, 0]}>
                  {chartData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <PriceSensitivity base={r} value={value} adj={adj} setAdj={setAdj} />
    </div>
  );
}

function PriceSensitivity({ base, value, adj, setAdj }) {
  const cogs = Number(value.cogs) || 0;
  const volume = Number(value.volume) || 0;
  const overhead = Number(value.overhead) || 0;
  const basePrice = Number(base.price) || 0;
  const adjPrice = Math.max(0, basePrice * (1 + adj / 100));
  const adjNet = (adjPrice - cogs) * volume - overhead;
  const baseNet = Number(base.netProfit) || 0;
  const delta = adjNet - baseNet;
  const marginPct = adjPrice > 0 ? ((adjPrice - cogs) / adjPrice) * 100 : 0;

  return (
    <div data-testid="price-sensitivity" className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
      <div className="flex items-center gap-2 mb-1">
        <Sliders className="h-5 w-5 text-orange-400" />
        <h3 className="font-display text-lg font-bold">Price sensitivity</h3>
      </div>
      <p className="text-sm text-slate-400 mb-5">Drag to see how moving your price changes monthly profit.</p>

      <div className="flex items-center justify-between mb-2 text-sm">
        <span className="text-slate-400">Adjust price</span>
        <span data-testid="sensitivity-adj" className={`font-semibold ${adj > 0 ? "text-emerald-400" : adj < 0 ? "text-rose-400" : "text-slate-200"}`}>
          {adj > 0 ? "+" : ""}{adj}%
        </span>
      </div>
      <Slider data-testid="sensitivity-slider" value={[adj]} min={-40} max={40} step={1} onValueChange={(v) => setAdj(v[0])} />

      <div className="grid grid-cols-3 gap-3 mt-6">
        <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
          <div className="text-xs text-slate-400">New price</div>
          <div data-testid="sensitivity-price" className="font-display text-lg font-bold text-orange-400 mt-1">${adjPrice.toFixed(2)}</div>
          <div className="text-xs text-slate-500 mt-0.5">{marginPct.toFixed(0)}% margin</div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
          <div className="text-xs text-slate-400">Monthly net profit</div>
          <div data-testid="sensitivity-net" className={`font-display text-lg font-bold mt-1 ${adjNet >= 0 ? "text-emerald-400" : "text-rose-400"}`}>${Math.round(adjNet).toLocaleString()}</div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
          <div className="text-xs text-slate-400">Vs. current</div>
          <div className={`font-display text-lg font-bold mt-1 ${delta >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{delta >= 0 ? "+" : "−"}${Math.abs(Math.round(delta)).toLocaleString()}</div>
        </div>
      </div>
    </div>
  );
}
